import {
  useApiInfiniteQuery,
  clearApiQuery,
  type Api,
} from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useActiveLang } from "../../app/hooks/useActiveLang";

export type ForYouFeedItem =
  Api["get"]["/webapp/private/feed"]["output"]["body"]["docs"][number];

type ForYouRecommendedContentQueryResult = {
  contentList: ForYouFeedItem[];
  contentListIds: string;
  isLoading: boolean;
  isFetching: boolean;
  // Unlike `canFetchNextPage`, stays true while a page loads: the end-of-feed
  // card reads this so a background refetch does not blink it away.
  hasNextPage: boolean;
  canFetchNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  clear: () => void;
};

export function useGetForYouRecommendedContentQuery(
  roomId: string | null,
  topicUid: string | null,
  // Phase 4 daily-refresh seed. null → server daily default; a client seed (from
  // useFeedSeed.refresh) re-keys the query so a refresh fetches a new feed.
  seed: string | null
): ForYouRecommendedContentQueryResult {
  const { currentUser } = useCurrentUser();
  const { contentLang } = useActiveLang();

  const query = useApiInfiniteQuery(
    "/webapp/private/feed",
    currentUser === null
      ? false
      : {
          query: {
            limit: 20,
            language: contentLang,
            roomId: roomId === null ? undefined : roomId,
            topicUid: topicUid === null ? undefined : topicUid,
            seed: seed === null ? undefined : seed,
          },
        }
  );

  const contentList: ForYouFeedItem[] =
    query.state === "success" ? query.docs : [];
  const contentListIds = contentList.map((content) => content.id).join(",");

  return {
    contentList,
    contentListIds,
    isLoading: query.state === "pending",
    isFetching: query.isFetching,
    hasNextPage: query.state === "success" ? query.hasNextPage : false,
    canFetchNextPage:
      query.state === "success" ? query.canFetchNextPage : false,
    isFetchingNextPage:
      query.state === "success" ? query.isFetchingNextPage : false,
    fetchNextPage:
      query.state === "success" ? query.fetchNextPage : () => void 0,
    clear: () => {
      clearApiQuery("/webapp/private/feed", {});
    },
  };
}

export function clearForYouContentNonHook() {
  clearApiQuery("/webapp/private/feed", {});
}
